import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, RefreshCw, Users, KeyRound, Layers3,
  UserPlus, Ban, LockKeyhole
} from "lucide-react";

const card = "rounded-xl border border-slate-200 bg-white p-4 shadow-sm";
const field = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm";
const button = "inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50";

function listOf(data, key) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.[key])) return data[key];
  return [];
}

function displayName(item) {
  return item?.label || item?.name || item?.title || item?.id || item?.role_id || "-";
}

export default function AccessControlPage() {
  const [roles, setRoles] = useState([]);
  const [permissionData, setPermissionData] = useState({});
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("assignments");
  const [form, setForm] = useState({
    employee_id: "",
    role_id: "",
    assignment_type: "PRIMARY",
    scope_type: "COMPANY",
    scope_id: "",
    start_date: "",
    end_date: "",
    reason: "",
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    const results = await Promise.allSettled([
      api.get("/rbac/roles"),
      api.get("/rbac/permissions"),
      api.get("/rbac/assignments?status=active"),
    ]);

    if (results[0].status === "fulfilled") {
      const rows = listOf(results[0].value.data, "roles");
      setRoles(rows);
      setForm((old) => ({
        ...old,
        role_id: old.role_id || rows[0]?.id || rows[0]?.role_id || "",
      }));
    } else {
      toast.error("Katalog role gagal dimuat. Periksa akses RBAC.");
    }

    if (results[1].status === "fulfilled") {
      setPermissionData(results[1].value.data || {});
    } else {
      toast.error("Katalog permission gagal dimuat.");
    }

    if (results[2].status === "fulfilled") {
      setAssignments(listOf(results[2].value.data, "assignments"));
    } else {
      setAssignments([]);
      toast.error("Daftar penugasan gagal dimuat. Periksa hak akses dan koneksi API.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const scopeTypes = Array.isArray(permissionData.scope_types)
    ? permissionData.scope_types
    : ["COMPANY", "BUSINESS_UNIT", "BRAND", "OUTLET", "WAREHOUSE", "DEPARTMENT", "EMPLOYEE"];

  const assignmentTypes = Array.isArray(permissionData.assignment_types)
    ? permissionData.assignment_types
    : ["PRIMARY", "SECONDARY", "TEMPORARY", "ACTING"];

  const submitAssignment = async (event) => {
    event.preventDefault();
    if (!form.employee_id.trim() || !form.role_id) {
      toast.error("Employee ID dan role wajib diisi.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        employee_id: form.employee_id.trim(),
        role_id: form.role_id,
        assignment_type: form.assignment_type,
        scope_type: form.scope_type || null,
        scope_id: form.scope_id.trim() || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        reason: form.reason.trim(),
      };
      await api.post("/rbac/assignments", payload);
      toast.success("Penugasan berhasil dibuat.");
      setForm((old) => ({
        ...old,
        employee_id: "",
        scope_id: "",
        start_date: "",
        end_date: "",
        reason: "",
      }));
      await loadData();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
        "Penugasan gagal. Pastikan employee ID benar, karyawan ada pada tenant ini, dan role/scope valid."
      );
    } finally {
      setSaving(false);
    }
  };

  const revokeAssignment = async (assignment) => {
    const id = assignment?.id || assignment?.assignment_id || assignment?._id;
    if (!id) {
      toast.error("ID penugasan tidak tersedia dari API; penugasan tidak diubah.");
      return;
    }
    if (!window.confirm("Cabut penugasan role ini? Tindakan ini akan mengubah akses karyawan.")) return;

    try {
      await api.delete(`/rbac/assignments/${encodeURIComponent(id)}`);
      toast.success("Penugasan berhasil dicabut.");
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Gagal mencabut penugasan.");
    }
  };

  const permissionEntries = Object.entries(permissionData.permissions || {});

  return (
    <div className="space-y-5 p-4 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-7 w-7 text-orange-600" />
            <h1 data-workspace-redundant-header className="text-2xl font-bold text-slate-900">Role & Hak Akses</h1>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Administrasi role, permission, penugasan, dan batas scope akses.
          </p>
        </div>
        <button className={button} onClick={loadData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Muat ulang
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className={card}>
          <KeyRound className="mb-2 h-5 w-5 text-orange-600" />
          <p className="text-sm text-slate-500">Role dari API</p>
          <p className="text-2xl font-bold">{roles.length}</p>
        </div>
        <div className={card}>
          <Layers3 className="mb-2 h-5 w-5 text-orange-600" />
          <p className="text-sm text-slate-500">Modul permission</p>
          <p className="text-2xl font-bold">{permissionEntries.length}</p>
        </div>
        <div className={card}>
          <Users className="mb-2 h-5 w-5 text-orange-600" />
          <p className="text-sm text-slate-500">Penugasan aktif</p>
          <p className="text-2xl font-bold">{assignments.length}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-3">
        {[
          ["assignments", "Penugasan Karyawan"],
          ["roles", "Katalog Role"],
          ["permissions", "Matriks Permission"],
        ].map(([value, label]) => (
          <button
            key={value}
            onClick={() => setTab(value)}
            className={`rounded-lg px-3 py-2 text-sm font-medium ${
              tab === value ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "assignments" && (
        <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <form className={`${card} space-y-4`} onSubmit={submitAssignment}>
            <div className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-orange-600" />
              <h2 className="font-semibold">Tambah Penugasan Role</h2>
            </div>
            <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
              Gunakan Employee ID yang benar-benar ada di tenant aktif. Halaman ini tidak membuat data karyawan baru.
            </div>
            <label className="block space-y-1">
              <span className="text-sm font-medium">Employee ID *</span>
              <input className={field} value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} placeholder="ID karyawan dari data HR" required />
            </label>
            <label className="block space-y-1">
              <span className="text-sm font-medium">Role *</span>
              <select className={field} value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value })} required>
                <option value="">Pilih role</option>
                {roles.map((role, index) => {
                  const id = role.id || role.role_id;
                  return id ? <option key={id} value={id}>{displayName(role)} ({id})</option> : null;
                })}
              </select>
              {roles.length === 0 && <span className="block text-xs text-amber-700">Katalog role kosong atau tidak tersedia dari endpoint. Penugasan dinonaktifkan sampai data role valid tersedia.</span>}
            </label>
            <label className="block space-y-1">
              <span className="text-sm font-medium">Jenis penugasan</span>
              <select className={field} value={form.assignment_type} onChange={(e) => setForm({ ...form, assignment_type: e.target.value })}>
                {assignmentTypes.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block space-y-1">
                <span className="text-sm font-medium">Scope type</span>
                <select className={field} value={form.scope_type} onChange={(e) => setForm({ ...form, scope_type: e.target.value })}>
                  {scopeTypes.map((type) => <option key={type} value={type}>{type}</option>)}
                </select>
              </label>
              <label className="block space-y-1">
                <span className="text-sm font-medium">Scope ID</span>
                <input className={field} value={form.scope_id} onChange={(e) => setForm({ ...form, scope_id: e.target.value })} placeholder="ID unit/outlet/gudang" />
              </label>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block space-y-1">
                <span className="text-sm font-medium">Tanggal mulai</span>
                <input type="date" className={field} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </label>
              <label className="block space-y-1">
                <span className="text-sm font-medium">Tanggal berakhir</span>
                <input type="date" className={field} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </label>
            </div>
            <label className="block space-y-1">
              <span className="text-sm font-medium">Alasan penugasan</span>
              <textarea className={field} rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Contoh: penugasan sesuai jabatan" />
            </label>
            <button className={button} type="submit" disabled={saving || loading || roles.length === 0}>
              <UserPlus className="h-4 w-4" />
              {saving ? "Menyimpan..." : "Simpan Penugasan"}
            </button>
          </form>

          <section className={card}>
            <h2 className="mb-3 font-semibold">Penugasan Aktif</h2>
            {loading ? <p className="text-sm text-slate-500">Memuat data...</p> :
              assignments.length === 0 ? (
                <div className="rounded-lg bg-slate-50 p-5 text-center">
                  <Users className="mx-auto mb-2 h-8 w-8 text-slate-400" />
                  <p className="font-medium text-slate-700">Belum ada penugasan aktif</p>
                  <p className="mt-1 text-sm text-slate-500">Ini tidak otomatis berarti RBAC gagal. Bisa jadi memang belum ada karyawan yang diberi role.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {assignments.map((item, index) => {
                    const id = item.id || item.assignment_id || item._id || `${item.employee_id}-${item.role_id}-${index}`;
                    return (
                      <div key={id} className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-slate-200 p-3">
                        <div className="min-w-0">
                          <p className="font-medium">{item.employee_name || item.employee_id || "Karyawan"}</p>
                          <p className="text-sm text-slate-600">Role: {item.role_id || "-"}</p>
                          <p className="text-xs text-slate-500">
                            {item.assignment_type || "-"} · {item.scope_type || "-"} · {item.scope_id || "tanpa ID scope"}
                          </p>
                          <p className="mt-1 text-xs text-slate-400">Status: {item.status || "active"}</p>
                        </div>
                        <button className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50" onClick={() => revokeAssignment(item)}>
                          <Ban className="h-3.5 w-3.5" /> Cabut akses
                        </button>
                      </div>
                    );
                  })}
                </div>
              )
            }
          </section>
        </div>
      )}

      {tab === "roles" && (
        <section className={card}>
          <h2 className="mb-3 font-semibold">Katalog Role</h2>
          {roles.length === 0 ? (
            <p className="text-sm text-slate-500">Endpoint tidak mengembalikan role. Periksa respons API sebelum mengubah konfigurasi.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead><tr className="border-b bg-slate-50"><th className="p-3">Role</th><th className="p-3">ID</th><th className="p-3">Scope default</th><th className="p-3">Deskripsi</th></tr></thead>
                <tbody>{roles.map((role, index) => (
                  <tr className="border-b last:border-0" key={role.id || role.role_id || index}>
                    <td className="p-3 font-medium">{displayName(role)}</td>
                    <td className="p-3">{role.id || role.role_id || "-"}</td>
                    <td className="p-3">{role.default_scope || role.scope_type || "-"}</td>
                    <td className="p-3">{role.description || role.desc || "-"}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
          <p className="mt-3 text-xs text-slate-500">Katalog ini bersifat baca-saja. Pengubahan definisi role belum diaktifkan.</p>
        </section>
      )}

      {tab === "permissions" && (
        <section className={card}>
          <div className="mb-3 flex items-center gap-2">
            <LockKeyhole className="h-5 w-5 text-orange-600" />
            <h2 className="font-semibold">Matriks Permission</h2>
          </div>
          {permissionEntries.length === 0 ? (
            <p className="text-sm text-slate-500">Format katalog permission dari API belum tersedia atau kosong.</p>
          ) : (
            <div className="space-y-3">
              {permissionEntries.map(([module, actions]) => (
                <div className="rounded-lg border border-slate-200 p-3" key={module}>
                  <p className="mb-2 font-semibold">{module}</p>
                  <div className="flex flex-wrap gap-2">
                    {(Array.isArray(actions) ? actions : typeof actions === "object" && actions !== null ? Object.entries(actions).map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : JSON.stringify(value)}`) : [String(actions)]).map((action, index) => (
                      <span className="rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-700" key={`${module}-${index}`}>{action}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="mt-3 text-xs text-slate-500">Permission ditampilkan sesuai respons backend; halaman ini tidak mengubah kebijakan akses.</p>
        </section>
      )}
    </div>
  );
}
